import { t } from '../i18n/lingua'
import { LogoCompleto } from '../components/Marchio'
import { useEffect, useState } from 'react'
import type { FormEvent } from 'react'

import { api } from '../api/client'
import { useAuth } from '../auth/AuthContext'
import { ErrorBanner } from '../components/common'

/**
 * Accesso alla console: email e password, nient'altro.
 *
 * C'era una terza casella con l'identificativo del tenant, un UUID: si poteva
 * entrare solo conoscendo in anticipo un valore che nessuno ricorda. Su quale
 * tenant operare è una domanda che viene dopo, e solo per chi ne ha più d'uno.
 */
export default function Login() {
  const { login, completeOidc, chooseTenant, pendingChoice, pendingMfa, completeMfa } = useAuth()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState<unknown>(null)
  const [busy, setBusy] = useState(false)
  const [codice, setCodice] = useState('')
  // Il reindirizzamento verso l'IdP: mentre risponde, la pagina non deve
  // mostrare il form che sta per lasciare — è il lampo che si vede oggi.
  const [reindirizzo, setReindirizzo] = useState<string | null>(null)
  // Il ritorno dall'IdP: se l'URL porta `code`+`state`, siamo di rientro e
  // stiamo scambiando il codice. Si legge SUBITO, al primo render, così la
  // schermata d'attesa c'è prima ancora dell'effetto — altrimenti si vede il
  // form per l'istante in cui `completeOidc` è in volo. È il lampo segnalato.
  const [scambioIdp, setScambioIdp] = useState<boolean>(() => {
    const p = new URLSearchParams(window.location.search)
    return Boolean(p.get('state') && p.get('code'))
  })
  // Il provider che serve il dominio scritto, se ce n'è uno.
  //
  // Si chiede mentre si scrive, non premendo un pulsante: chi ha un IdP
  // aziendale non deve sapere di averlo — vede comparire «entra con…» e capisce.
  const [provider, setProvider] = useState<string | null>(null)

  // Dove l'IdP rimanda il browser. Lo dichiara la console perché è la console a
  // sapere su quale indirizzo la stanno guardando: dedurlo dagli header lato
  // server significherebbe fidarsi di un proxy per costruire un URL che l'IdP
  // confronta con quello registrato — e uno diverso fa fallire l'accesso con un
  // errore che parla d'altro.
  const redirectUri = `${window.location.origin}/login`

  // Il ritorno dall'IdP: `state` e `code` nella query.
  //
  // Si guarda **una volta sola** all'apertura, e si ripulisce l'indirizzo:
  // lasciarli lì significa che un aggiornamento della pagina riprova un codice
  // già speso, e l'utente vede un errore su un accesso che era riuscito.
  useEffect(() => {
    const parametri = new URLSearchParams(window.location.search)
    const state = parametri.get('state')
    const code = parametri.get('code')
    if (!state || !code) return

    window.history.replaceState({}, '', window.location.pathname)
    completeOidc(state, code).catch((err) => {
      // Solo qui si torna al form: in caso di successo `principal` cambia e
      // questa pagina lascia il posto alla console.
      setError(err)
      setScambioIdp(false)
    })
  }, [completeOidc])

  // Il passo del login identifier-first: prima l'indirizzo, poi «Avanti», e
  // solo allora si scopre se quel dominio ha un IdP. Chiedere email e password
  // insieme costringe a scrivere una password anche a chi entrerà con l'SSO;
  // rilevare a ogni tasto era una richiesta per carattere.
  const [passo, setPasso] = useState<'email' | 'metodo'>('email')

  async function avanti(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      const r = await api.oidcStart(email.trim(), redirectUri)
      setProvider(r.provider_name)
      setPasso('metodo')
    } catch (err) {
      // Un dominio senza IdP non è un errore: `oidc/start` risponde comunque,
      // con provider nullo. Qui si arriva solo se la chiamata stessa fallisce.
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function entraConProvider() {
    setError(null)
    // La schermata di attesa parte SUBITO, non dopo la risposta di oidcStart:
    // il form sparisce al clic, non quando l'IdP si degna di rispondere.
    setReindirizzo(provider)
    try {
      const r = await api.oidcStart(email.trim(), redirectUri)
      if (!r.authorization_url) throw new Error(t('provider non disponibile'))
      window.location.href = r.authorization_url
    } catch (err) {
      // Solo se qualcosa va storto si torna al form: in caso di successo la
      // pagina naviga via e la schermata resta fino all'IdP.
      setReindirizzo(null)
      setError(err)
    }
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      await login(email.trim(), password)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  async function onChoose(tenantId: string) {
    setError(null)
    setBusy(true)
    try {
      await chooseTenant(tenantId)
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }

  // Il reindirizzamento all'IdP: sostituisce il form finché il browser non
  // parte verso il provider. Un'attesa dichiarata è meglio di un form che
  // lampeggia e sparisce.
  // L'attesa cede il passo alla scelta del tenant e al secondo fattore: dopo
  // lo scambio del codice `completeOidc` può chiedere su quale tenant entrare,
  // e se i pallini restassero davanti quella scelta non comparirebbe mai — i
  // pallini all'infinito. Il successo a tenant singolo porta via la pagina da
  // sé (cambia `principal`), quindi qui non serve spegnerli a mano.
  if ((reindirizzo || scambioIdp) && !pendingChoice && !pendingMfa) {
    return (
      <div className="login">
        <div className="login__box" style={{ textAlign: 'center' }}>
          <div className="login__marchio"><LogoCompleto size={56} /></div>
          <div className="sso-attesa" aria-hidden="true">
            <span /><span /><span />
          </div>
          <p className="login__tagline" role="status">
            {reindirizzo
              ? t('Ti sto portando su {provider}…', { provider: reindirizzo })
              : t('Accesso in corso…')}
          </p>
        </div>
      </div>
    )
  }

  // Il secondo fattore: la password è giusta, manca il codice dell'app.
  if (pendingMfa) {
    return (
      <div className="login">
        <form
          className="login__box"
          onSubmit={(e) => {
            e.preventDefault()
            setError(null)
            setBusy(true)
            completeMfa(codice.trim())
              .catch(setError)
              .finally(() => setBusy(false))
          }}
        >
          <div className="login__marchio"><LogoCompleto size={56} /></div>
          <p className="login__prodotto">CENSO</p>
          <p className="login__tagline">{t('Il codice dell’app di autenticazione')}</p>
          <ErrorBanner error={error} />
          <div className="field">
            <label htmlFor="codice-mfa">{t('Codice a 6 cifre')}</label>
            <input
              id="codice-mfa"
              value={codice}
              onChange={(e) => setCodice(e.target.value)}
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              style={{ letterSpacing: '0.3em', textAlign: 'center', fontSize: 20 }}
            />
          </div>
          <button type="submit" disabled={busy || codice.trim().length < 6} style={{ width: '100%' }}>
            {busy ? t('Verifica…') : t('Verifica')}
          </button>
        </form>
      </div>
    )
  }

  // Secondo passaggio: le credenziali sono state accettate, resta da dire su
  // quale cliente si sta per lavorare.
  if (pendingChoice) {
    return (
      <div className="login">
        <div className="login__box">
          <div className="login__marchio"><LogoCompleto size={56} /></div>
          <p className="login__prodotto">CENSO</p>
          <p className="login__tagline">{t('Su quale tenant vuoi operare?')}</p>

          <ErrorBanner error={error} />

          <div className="tenant-choice">
            {pendingChoice.tenants.map((tenant) => (
              <button
                key={tenant.tenant_id}
                type="button"
                className="tenant-choice__item"
                onClick={() => onChoose(tenant.tenant_id)}
                disabled={busy}
              >
                <span className="tenant-choice__name">{tenant.name}</span>
                <span className="tenant-choice__slug mono" title={tenant.slug}>{tenant.slug}</span>
                <span className="tenant-choice__roles">
                  {/* I ruoli valgono su questo tenant e possono essere diversi
                      sugli altri: mostrarli qui evita di scoprirlo provando. */}
                  {tenant.roles.join(', ') || t('nessun ruolo')}
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="login">
      <form
        className="login__box"
        onSubmit={passo === 'email' ? avanti : onSubmit}
      >
        <div className="login__marchio"><LogoCompleto size={72} /></div>
        <p className="login__prodotto">CENSO</p>
        <p className="login__tagline">{t('Asset Discovery & Baseline System')}</p>

        <ErrorBanner error={error} />

        <div className="field">
          <label htmlFor="email">{t('Email')}</label>
          <input
            id="email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            autoComplete="username"
            required
            disabled={passo === 'metodo'}
          />
          {passo === 'metodo' && (
            <button
              type="button"
              className="linklike"
              style={{ fontSize: 12, marginTop: 4 }}
              onClick={() => {
                // Torna al primo passo: cambiare indirizzo azzera il metodo,
                // altrimenti si mostrerebbe l'IdP del dominio precedente.
                setPasso('email')
                setProvider(null)
                setPassword('')
                setError(null)
              }}
            >
              {t('cambia indirizzo')}
            </button>
          )}
        </div>

        {passo === 'email' && (
          <button type="submit" disabled={busy || !email.trim()} style={{ width: '100%' }}>
            {busy ? t('Controllo…') : t('Avanti')}
          </button>
        )}

        {passo === 'metodo' && (
          <>
            {/* Se il dominio ha un IdP lo offro per primo — è la strada che
                l'organizzazione ha scelto — ma lascio comunque la password:
                un amministratore locale deve poter entrare anche quando l'IdP
                è giù. Senza IdP, solo la password. */}
            {provider && (
              <>
                <button
                  type="button"
                  disabled={busy}
                  onClick={entraConProvider}
                  style={{ width: '100%' }}
                >
                  {t('Entra con {provider}', { provider })}
                </button>
                <p className="login__tagline" style={{ margin: '14px 0 8px' }}>
                  {t('oppure con la password')}
                </p>
              </>
            )}
            <div className="field">
              <label htmlFor="password">{t('Password')}</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                autoFocus={!provider}
                required
              />
            </div>
            <button
              type="submit"
              disabled={busy || !password}
              className={provider ? 'secondary' : undefined}
              style={{ width: '100%' }}
            >
              {busy ? t('Accesso in corso…') : t('Accedi')}
            </button>
          </>
        )}
      </form>
    </div>
  )
}
